<template>
  <div class="select-search-container" :style="containerStyle">
    <input
      type="text"
      :id="id"
      v-model="value"
      @input="handleSearch"
      @focus="handleFocus"
      :placeholder="placeholder"
      :class="getInputClass()"
      :style="inputStyle"
      autocomplete="off"
    />
    <div v-if="isDropdownVisible" class="select-search-dropdown">
      <div
        v-for="(option, index) in options"
        :key="option.id ?? index"
        @click="handleSelectOption(option)"
        class="select-search-dropdown-item"
      >
        {{ option.name }}
      </div>
      <div v-if="options.length === 0" class="select-search-not-found-item">
        No results
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'SelectSearch',
  data() {
    return {
      value: '',
      selectedValue: '',
      isDropdownVisible: false,
      timeout: undefined as any,
    };
  },
  emits: [
    'search',
    'select',
    'resetOptions',
    'dropdownVisibleChange'
  ],
  props: {
    id: {
      type: String,
      required: true,
    },
    defaultValue: {
      type: String,
      required: false,
      default: ''
    },
    placeholder: {
      type: String,
      required: false,
      default: 'Please select'
    },
    options: {
      type: Array<any>,
      required: true,
    },
    triggerResetValueFlag: {
      type: Boolean,
      required: false,
    },
    inputClass: {
      type: String,
      required: false,
      default: 'span12'
    },
    inputStyle: {
      type: String,
      required: false,
      default: ''
    },
    containerStyle: {
      type: String,
      required: false,
      default: ''
    },
  },
  methods: {
    handleSearch() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        if (this.isDropdownVisible) {
          // support languages with complex character input
          let inputValue = (document.getElementById(this.id) as HTMLInputElement).value;
          this.$emit('search', inputValue?.trim() || '');
        }
      }, 300);
    },
    handleFocus() {
      this.handleChangeDropdownVisible(true);
    },
    handleSelectOption(option: any) {
      this.selectedValue = option.name;
      this.value = this.selectedValue;
      this.handleChangeDropdownVisible(false);

      this.$emit('select', option);
      this.$emit('resetOptions');
    },
    handleClickOutside(event: any) {
      const searchInput = document.querySelector(`#${this.id}`);
      if (searchInput && searchInput !== event.target) {
        this.value = this.selectedValue;
        this.handleChangeDropdownVisible(false);
        this.$emit('resetOptions');
      }
    },
    handleResetValueToDefault() {
      this.selectedValue = this.defaultValue;
      this.value = this.defaultValue || '';
    },
    handleChangeDropdownVisible(visible: boolean) {
      this.isDropdownVisible = visible;
      this.$emit('dropdownVisibleChange', this.isDropdownVisible);
    },
    getInputClass(): string {
      return `select-search-input ${this.inputClass}`;
    },
  },
  mounted() {
    this.handleResetValueToDefault();
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  watch: {
    defaultValue() {
      this.handleResetValueToDefault();
    },
    triggerResetValueFlag() {
      this.handleResetValueToDefault();
    }
  },
});
</script>

<style scoped>
.select-search-container {
  position: relative;
}

.select-search-input {
  padding: 4px 11px;
}

.select-search-dropdown {
  position: absolute;
  top: 90%;
  left: 0;
  width: 100%;
  border: 1px solid #ccc;
  background-color: white;
  max-height: 200px;
  overflow-y: auto;
  z-index: 21;
}

.select-search-dropdown-item {
  cursor: pointer;
  font-weight: normal;
  display: block;
  padding-block-start: 0px;
  padding-block-end: 1px;
  min-block-size: 1.2em;
  padding-inline: 2px;
  line-height: 18px;
  padding: 4px 8px;
}

.select-search-dropdown-item:hover {
  color: #fff;
  background-color: rgb(109, 109, 109);
}

.select-search-not-found-item {
  cursor: default;
  font-weight: normal;
  display: block;
  padding-block-start: 0px;
  padding-block-end: 1px;
  min-block-size: 1.2em;
  padding-inline: 2px;
  line-height: 18px;
  padding: 4px 8px;
}
</style>


